import { useState } from "react";
import { Mail, Phone, MapPin, MessageCircle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import Overline from "@/components/Overline";
import { COMPANY } from "@/data/site";
import { submitContactInquiry } from "@/lib/api";

const initialForm = {
  name: "",
  email: "",
  phone: "",
  company: "",
  subject: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Please enter your name.";
    if (!form.email.trim()) er.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      er.email = "Please enter a valid email.";
    if (!form.message.trim()) er.message = "Please enter a message.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await submitContactInquiry(form);
      toast.success("Thank you — we'll get back to you within 24 hours.");
      setForm(initialForm);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or email us directly.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const waNumber = COMPANY.whatsapp.replace(/[^0-9]/g, "");

  return (
    <div data-testid="contact-page" className="border-b border-slate-200">
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="tech-grid tech-grid-fade absolute inset-0 opacity-50" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-28">
          <Overline>Contact</Overline>
          <h1 className="font-heading mt-6 max-w-3xl text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            Let&apos;s talk about your technology supply.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-700 sm:text-lg">
            Send us a message — sales, projects, partnerships or general
            enquiries. Our team typically responds within one business day.
          </p>
        </div>
      </section>

      <section>
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-20 sm:px-8 lg:grid-cols-12 lg:gap-14 lg:py-24">
          <div className="space-y-6 lg:col-span-5">
            <ContactItem
              icon={MapPin}
              testid="contact-address"
              label="Office"
              value={
                <>
                  {COMPANY.address.line1}
                  <br />
                  {COMPANY.address.line2}
                  <br />
                  {COMPANY.address.city}, {COMPANY.address.country}
                </>
              }
            />
            <ContactItem
              icon={Phone}
              testid="contact-phone"
              label="Phone"
              value={
                <a
                  href={`tel:${COMPANY.phone}`}
                  className="hover:text-cyan-700"
                >
                  {COMPANY.phone}
                </a>
              }
            />
            <ContactItem
              icon={Mail}
              testid="contact-email"
              label="Email"
              value={
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="hover:text-cyan-700"
                >
                  {COMPANY.email}
                </a>
              }
            />
            <ContactItem
              icon={MessageCircle}
              testid="contact-whatsapp"
              label="WhatsApp"
              value={
                <a
                  href={`https://wa.me/${waNumber}`}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-cyan-700"
                >
                  {COMPANY.whatsappDisplay}
                </a>
              }
            />

            <div className="mt-10 rounded-xl border border-slate-200 bg-slate-50 p-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-cyan-600">
                Business Hours
              </div>
              <div className="mt-3 space-y-1 text-sm text-slate-700">
                <div>Monday – Friday: 09:00 – 18:00 GST</div>
                <div>Saturday – Sunday: Closed</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              noValidate
              className="space-y-5 rounded-2xl border border-slate-200 bg-slate-50 p-7 sm:p-9"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <Field
                  label="Full name"
                  name="name"
                  testid="contact-form-name"
                  value={form.name}
                  onChange={handleChange}
                  error={errors.name}
                  required
                />
                <Field
                  label="Email address"
                  name="email"
                  type="email"
                  testid="contact-form-email"
                  value={form.email}
                  onChange={handleChange}
                  error={errors.email}
                  required
                />
                <Field
                  label="Phone"
                  name="phone"
                  testid="contact-form-phone"
                  value={form.phone}
                  onChange={handleChange}
                  optional
                />
                <Field
                  label="Company"
                  name="company"
                  testid="contact-form-company"
                  value={form.company}
                  onChange={handleChange}
                  optional
                />
              </div>

              <Field
                label="Subject"
                name="subject"
                testid="contact-form-subject"
                value={form.subject}
                onChange={handleChange}
                optional
              />

              <div className="space-y-2">
                <Label
                  htmlFor="message"
                  className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500"
                >
                  Message <span className="text-cyan-600">*</span>
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  rows={5}
                  data-testid="contact-form-message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell us about your requirement, quantities, brands, destination…"
                  className="border-slate-200 bg-white text-slate-800 placeholder:text-slate-500 focus-visible:ring-cyan-400/40"
                />
                {errors.message && (
                  <p
                    data-testid="contact-form-message-error"
                    className="text-xs text-red-400"
                  >
                    {errors.message}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                <p className="text-xs text-slate-500">
                  By submitting, you agree to be contacted about your enquiry.
                </p>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-form-submit-btn"
                  className="rounded-md bg-cyan-600 px-6 py-5 font-medium text-white hover:bg-cyan-700 disabled:opacity-70"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Send message
                      <Send className="ml-1.5 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({
  label,
  name,
  type = "text",
  value,
  onChange,
  error,
  testid,
  required = false,
  optional = false,
}) {
  return (
    <div className="space-y-2">
      <Label
        htmlFor={name}
        className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500"
      >
        {label}
        {required && <span className="text-cyan-600"> *</span>}
        {optional && <span className="ml-1 text-slate-400">(optional)</span>}
      </Label>
      <Input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="border-slate-200 bg-white text-slate-800 placeholder:text-slate-500 focus-visible:ring-cyan-400/40"
      />
      {error && (
        <p data-testid={`${testid}-error`} className="text-xs text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}

function ContactItem({ icon: Icon, label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex items-start gap-4 rounded-xl border border-slate-200 bg-slate-50 p-5"
    >
      <span className="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-md border border-cyan-500/40 bg-white text-cyan-700">
        <Icon className="h-4 w-4" />
      </span>
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-600">
          {label}
        </div>
        <div className="mt-1.5 text-sm leading-relaxed text-slate-700">
          {value}
        </div>
      </div>
    </div>
  );
}
